import { Box, Stack, Theme } from '@mui/material';
import { FC, useLayoutEffect, useState } from 'react';
import ResizeScrollbars from '@/components/resize-scrollbars';
import Loader from '../../loader';
import { createUseStyles } from 'react-jss';
import Text from '../../text';
import { getBreakpoint } from '@/theme';
import SidebarListItem from './list-item';
import SidebarListItemHeader from './list-item-header';
import { SidebarListItemProps } from './helpers';
import classNames from 'classnames';
import { LoadState } from '@/lib/helpers';

interface Props {
  isLoading: boolean;
  items: SidebarListItemProps[];
  emptyMessage?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  list: {
    flexGrow: 1,
    maxHeight: '100%',
    overflowX: 'hidden',
    color: theme.palette.text.primary,
    '& .header:not(.first)': {
      marginTop: 16,
    },
  },
  scrollContainer: {
    overflow: 'visible',
    gap: 4,
  },
  [getBreakpoint('md')]: {
    list: {
      paddingLeft: 8,
    },
    scrollContainer: {
      paddingRight: 12,
    },
  },
}));

const SidebarList: FC<Props> = ({ isLoading, items, emptyMessage = '' }) => {
  const styles = useStyles();
  const hasItems = !!items.length;

  const [expanded, setExpanded] = useState<Map<string, boolean>>(new Map());
  const [loadState, setLoadState] = useState<LoadState>('loading');

  const handleClick = (section: string) => {
    setExpanded((old) => {
      const updated = new Map(old);
      updated.set(section, !updated.get(section));
      return updated;
    });
  };

  useLayoutEffect(() => {
    setExpanded((old) => {
      const updated: Map<string, boolean> = new Map();
      items.forEach(({ header, expand, key }) => {
        if (!header || !expand) {
          return;
        }

        // Preserve the user's expanded state across list refreshes.
        updated.set(key, old.get(key) ?? false);
      });
      return updated;
    });

    setLoadState(items.length ? 'loaded' : 'loading');
  }, [items]);

  if (loadState !== 'loaded') {
    return null;
  }

  let sectionKey = '';

  return (
    <Box className={styles.list}>
      <ResizeScrollbars>
        <Stack className={styles.scrollContainer}>
          {isLoading && (
            <Stack direction="row" justifyContent="center" width="90%" mx="auto">
              <Loader />
            </Stack>
          )}

          {!isLoading && !hasItems && (
            <Box mt={1} ml={2}>
              <Text italic size="small">
                {emptyMessage}
              </Text>
            </Box>
          )}

          {!isLoading &&
            items.map((item, index) => {
              const { header, title, key, icon, expand } = item;

              if (header) {
                sectionKey = key;
                return (
                  <SidebarListItemHeader
                    title={title}
                    key={key}
                    icon={icon}
                    expanded={!expand || expanded.get(key)}
                    className={classNames('header', { first: !index })}
                    onClick={expand ? () => handleClick(key) : undefined}
                  />
                );
              }

              if (expanded.has(sectionKey) && !expanded.get(sectionKey)) {
                return null;
              }

              return <SidebarListItem {...item} key={key} />;
            })}
        </Stack>
      </ResizeScrollbars>
    </Box>
  );
};

export default SidebarList;
